/**
 * RecurrenceField - Visual field for schedule/recurrence configuration
 *
 * Provides UI for configuring trigger schedules with:
 * - Frequency selection (Month, Week, Day, Hour, Minute, Second)
 * - Interval configuration
 * - Advanced options: Start time, timezone, schedule details
 * - Real-time preview of the schedule
 *
 * This component replaces the legacy RecurrenceEditor and works with
 * raw RecurrenceValue objects (no ValueSegment serialization).
 */

import {
  Dropdown,
  Option,
  SpinButton,
  Input,
  ToggleButton,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  Text,
  Caption1,
  Field,
  Tag,
  TagGroup,
  Button,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import type {
  TagGroupProps,
  SpinButtonChangeEvent,
  SpinButtonOnChangeData,
  OptionOnSelectData,
} from '@fluentui/react-components';
import { AddRegular } from '@fluentui/react-icons';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { DAY_ABBREVS, formatHour } from '@/utils/dateTime';
import type { RecurrenceValue, Frequency, WeekDay } from '@/types/recurrence';
import {
  FrequencyValues,
  WeekDayValues,
  COMMON_TIMEZONES,
  supportsSchedule,
  supportsWeekDays,
  supportsMonthDays,
  generateRecurrencePreview,
  getFrequencyLabel,
  DEFAULT_RECURRENCE,
} from '@/types/recurrence';
import { validateRecurrence } from '@/services/recurrenceSerializationService';
import { isLocalMode } from '@/services/localRuntimeClient';
import { DateTimePicker } from '@/components/DateTimePicker';
import { SearchableCombobox, type SearchableComboboxOption } from '@/components/shared';
import { useRecurrenceFieldStyles } from './RecurrenceField.styles';
import {
  addScheduleItem,
  removeScheduleItem,
  toggleWeekDay,
} from '@/utils/recurrence';

const FREQUENCY_OPTIONS = FrequencyValues.map((freq) => (
  <Option key={freq} value={freq}>{freq}</Option>
));

const TIMEZONE_OPTIONS: SearchableComboboxOption[] = COMMON_TIMEZONES.map((tz) => ({
  value: tz.value,
  text: tz.displayName,
}));

export interface RecurrenceFieldProps {
  /** Stable identifier (e.g. parameter name). */
  name?: string | undefined;
  /** Current recurrence value (uses DEFAULT_RECURRENCE when undefined). */
  value: RecurrenceValue | undefined;
  /** Emits the updated recurrence whenever the user edits a field. */
  onChange: (value: RecurrenceValue) => void;
  disabled?: boolean | undefined;
  readOnly?: boolean | undefined;
}

/**
 * RecurrenceField component
 */
export const RecurrenceField = ({
  value,
  onChange,
  disabled = false,
  readOnly = false,
}: RecurrenceFieldProps) => {
  const styles = useRecurrenceFieldStyles();

  // Use the provided value or fall back to default
  const recurrence = useMemo((): RecurrenceValue => value ?? DEFAULT_RECURRENCE, [value]);

  // Local state for adding new hours/minutes
  const [newHour, setNewHour] = useState<string>('');
  const [newMinute, setNewMinute] = useState<string>('');
  const [newMonthDay, setNewMonthDay] = useState<string>('');
  const [startTimeError, setStartTimeError] = useState<string | undefined>(undefined);

  // Local mirror of `interval` so the preview updates while typing without
  // committing every keystroke (we only commit on a valid clamped value).
  const [localInterval, setLocalInterval] = useState(recurrence.interval);
  const [intervalDisplayValue, setIntervalDisplayValue] = useState<string>(String(recurrence.interval));
  useEffect(() => {
    setLocalInterval(recurrence.interval);
    setIntervalDisplayValue(String(recurrence.interval));
  }, [recurrence.interval]);

  const errors = useMemo(() => validateRecurrence(recurrence), [recurrence]);

  const handleFrequencyChange = useCallback(
    (_e: unknown, data: OptionOnSelectData) => {
      const newFrequency = data.optionValue as Frequency;
      const updated: RecurrenceValue = {
        ...recurrence,
        frequency: newFrequency,
      };

      // Drop schedule fields the new frequency doesn't support.
      if (updated.schedule) {
        const newSchedule = { ...updated.schedule };
        if (!supportsWeekDays(newFrequency)) {
          delete newSchedule.weekDays;
        }
        if (!supportsMonthDays(newFrequency)) {
          delete newSchedule.monthDays;
        }
        if (!supportsSchedule(newFrequency)) {
          delete newSchedule.hours;
          delete newSchedule.minutes;
        }
        if (Object.keys(newSchedule).length > 0) {
          updated.schedule = newSchedule;
        } else {
          delete updated.schedule;
        }
      }

      onChange(updated);
    },
    [recurrence, onChange]
  );

  const handleIntervalChange = useCallback(
    (_e: SpinButtonChangeEvent, data: SpinButtonOnChangeData) => {
      if (data.displayValue !== undefined) {
        setIntervalDisplayValue(data.displayValue);
      }
      const newInterval = data.value
        ?? (data.displayValue ? parseInt(data.displayValue, 10) : NaN);
      if (isNaN(newInterval) || newInterval < 1) return;
      const clamped = Math.max(1, Math.min(1000, newInterval));
      setLocalInterval(clamped);
      onChange({ ...recurrence, interval: clamped });
    },
    [recurrence, onChange]
  );

  const handleStartTimeSelect = useCallback(
    (selectedTime: string) => {
      const updated: RecurrenceValue = { ...recurrence, startTime: selectedTime };
      onChange(updated);
    },
    [recurrence, onChange]
  );

  const handleTimezoneChange = useCallback(
    (value: string) => {
      const updated: RecurrenceValue = { ...recurrence };
      if (value) {
        updated.timeZone = value;
      } else {
        delete updated.timeZone;
      }
      onChange(updated);
    },
    [recurrence, onChange]
  );

  const handleWeekDayToggle = useCallback(
    (day: WeekDay, checked: boolean) => {
      onChange(toggleWeekDay(recurrence, day, checked));
    },
    [recurrence, onChange]
  );

  const addHour = useCallback(() => {
    const hour = parseInt(newHour, 10);
    if (isNaN(hour) || hour < 0 || hour > 23) return;
    onChange(addScheduleItem(recurrence, 'hours', hour));
    setNewHour('');
  }, [newHour, recurrence, onChange]);

  const removeHour = useCallback(
    (hour: number) => onChange(removeScheduleItem(recurrence, 'hours', hour)),
    [recurrence, onChange]
  );

  const addMinute = useCallback(() => {
    const minute = parseInt(newMinute, 10);
    if (isNaN(minute) || minute < 0 || minute > 59) return;
    onChange(addScheduleItem(recurrence, 'minutes', minute));
    setNewMinute('');
  }, [newMinute, recurrence, onChange]);

  const removeMinute = useCallback(
    (minute: number) => onChange(removeScheduleItem(recurrence, 'minutes', minute)),
    [recurrence, onChange]
  );

  const addMonthDay = useCallback(() => {
    const day = parseInt(newMonthDay, 10);
    if (isNaN(day) || day < -1 || day > 31 || day === 0) return;
    onChange(addScheduleItem(recurrence, 'monthDays', day));
    setNewMonthDay('');
  }, [newMonthDay, recurrence, onChange]);

  const removeMonthDay = useCallback(
    (day: number) => onChange(removeScheduleItem(recurrence, 'monthDays', day)),
    [recurrence, onChange]
  );

  const handleMonthDayDismiss = useCallback<NonNullable<TagGroupProps['onDismiss']>>(
    (_e, data) => {
      if (data.value !== '') removeMonthDay(parseInt(data.value, 10));
    },
    [removeMonthDay]
  );

  const handleHourDismiss = useCallback<NonNullable<TagGroupProps['onDismiss']>>(
    (_e, data) => {
      if (data.value !== '') removeHour(parseInt(data.value, 10));
    },
    [removeHour]
  );

  const handleMinuteDismiss = useCallback<NonNullable<TagGroupProps['onDismiss']>>(
    (_e, data) => {
      if (data.value !== '') removeMinute(parseInt(data.value, 10));
    },
    [removeMinute]
  );

  // `localInterval` keeps the preview responsive while the user is still
  // typing into the SpinButton (before a valid clamped value commits).
  const previewText = useMemo(
    () => generateRecurrencePreview({ ...recurrence, interval: localInterval }),
    [recurrence, localInterval]
  );

  const isDisabled = disabled || readOnly;

  return (
    <div className={styles.container}>
      {/* Local mode warning: recurrence triggers need table storage */}
      {isLocalMode() && (
        <MessageBar intent="warning" className={styles.warningBar}>
          <MessageBarBody className={styles.warningBarBody}>
            Recurrence triggers require table storage to fire locally. Start the runtime with{' '}
              <code className={styles.inlineCode}>--use-table-storage</code> (or set{' '}
              <code className={styles.inlineCode}>STD_USE_TABLE_STORAGE=true</code>), with Azurite enabled.
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Frequency and Interval Row */}
      <div className={styles.row}>
        <Field label="Frequency" required className={styles.frequencyField}>
          <Dropdown
            value={recurrence.frequency}
            selectedOptions={[recurrence.frequency]}
            onOptionSelect={handleFrequencyChange}
            disabled={isDisabled}
            aria-label="Frequency"
            aria-required="true"
          >
            {FREQUENCY_OPTIONS}
          </Dropdown>
        </Field>

        <Field label="Interval" required className={styles.intervalField}>
          <SpinButton
            value={localInterval}
            displayValue={intervalDisplayValue}
            onChange={handleIntervalChange}
            min={1}
            max={1000}
            disabled={isDisabled}
            aria-label="Interval"
            aria-describedby="interval-unit"
            aria-required="true"
          />
        </Field>

        <Text className={styles.unitLabel} id="interval-unit">{getFrequencyLabel(recurrence.frequency, localInterval)}</Text>
      </div>

      {/* Preview */}
      <div className={styles.preview} role="status" aria-live="polite">
        <Text className={styles.previewText}>{previewText}</Text>
      </div>

      {/* Start time + Time zone — surfaced at the top level (not behind
          Advanced Options) so they're easy to discover. Mirrors Logic
          Apps' ScheduleEditor: both are optional. */}
      <Field
        label="Start time"
        hint="When to start the schedule"
        {...(startTimeError ? { validationMessage: startTimeError, validationState: 'error' as const } : {})}
      >
        <DateTimePicker
          onSelect={handleStartTimeSelect}
          setError={setStartTimeError}
          initialValue={recurrence.startTime}
          disabled={isDisabled}
        />
      </Field>

      <Field label="Time zone">
        <SearchableCombobox
          options={TIMEZONE_OPTIONS}
          value={recurrence.timeZone ?? ''}
          onChange={handleTimezoneChange}
          disabled={isDisabled}
          placeholder="Select timezone..."
          aria-label="Timezone"
          positioning={{ autoSize: false }}
          listbox={{ className: styles.timezoneListbox }}
        />
      </Field>

      {/* Advanced Options — only render when there's schedule detail to
          show. Sub-second/minute/hour frequencies have nothing here. */}
      {supportsSchedule(recurrence.frequency) && (
      <Accordion collapsible>
        <AccordionItem value="advanced">
          <AccordionHeader>Advanced Options</AccordionHeader>
          <AccordionPanel>
            <div className={styles.advancedSection}>
              {/* Schedule Details - only for Day, Week, Month */}
              {supportsSchedule(recurrence.frequency) && (
                <>
                  <Text size={200} weight="semibold" className={styles.scheduleDivider}>
                    Schedule
                  </Text>
                  <div className={styles.scheduleSection}>
                    {/* Week Days - only for Week frequency */}
                    {supportsWeekDays(recurrence.frequency) && (
                      <Field label="On these days">
                        <div className={styles.weekDaysContainer} role="group" aria-label="Days of the week">
                          {WeekDayValues.map((day) => {
                            const isSelected = recurrence.schedule?.weekDays?.includes(day) ?? false;
                            return (
                              <ToggleButton
                                key={day}
                                className={styles.dayToggle}
                                checked={isSelected}
                                onClick={() => handleWeekDayToggle(day, !isSelected)}
                                disabled={isDisabled}
                                aria-label={day}
                                shape="circular"
                                size="small"
                                appearance={isSelected ? 'primary' : 'outline'}
                              >
                                {DAY_ABBREVS[day]}
                              </ToggleButton>
                            );
                          })}
                        </div>
                      </Field>
                    )}

                    {/* Month Days - only for Month frequency */}
                    {supportsMonthDays(recurrence.frequency) && (
                      <Field label="On day(s) of month" hint="Enter day numbers (1-31, -1 for last day)">
                        <div className={styles.tagsContainer} role="group" aria-label="Selected days of month">
                          {recurrence.schedule?.monthDays && recurrence.schedule.monthDays.length > 0 && (
                            <div className={styles.tagsRow}>
                              <TagGroup onDismiss={handleMonthDayDismiss}>
                                {recurrence.schedule.monthDays.map((day) => (
                                  <Tag
                                    key={day}
                                    value={day.toString()}
                                    shape="circular"
                                    dismissible
                                    dismissIcon={{ 'aria-label': `Remove day ${day}` }}
                                  >
                                    {day === -1 ? 'Last' : day}
                                  </Tag>
                                ))}
                              </TagGroup>
                            </div>
                          )}
                          <div className={styles.inputRow}>
                            <Input
                              className={styles.numberInput}
                              type="number"
                              min={-1}
                              max={31}
                              value={newMonthDay}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '' || v === '-' || (v.length <= 3 && !isNaN(Number(v)))) setNewMonthDay(v);
                              }}
                              onKeyDown={(e) => e.key === 'Enter' && addMonthDay()}
                              disabled={isDisabled}
                              placeholder="Day"
                              autoComplete="off"
                              aria-label="Add day of month"
                            />
                            <Button
                              className={styles.addButton}
                              icon={<AddRegular />}
                              onClick={addMonthDay}
                              disabled={isDisabled}
                              aria-label="Add day of month"
                            />
                          </div>
                        </div>
                      </Field>
                    )}

                    {/* Hours */}
                    <Field label="At these hours" hint="Enter hours (0-23)">
                      <div className={styles.tagsContainer} role="group" aria-label="Selected hours">
                        {recurrence.schedule?.hours && recurrence.schedule.hours.length > 0 && (
                          <div className={styles.tagsRow}>
                            <TagGroup onDismiss={handleHourDismiss}>
                              {recurrence.schedule.hours.map((hour) => (
                                <Tag
                                  key={hour}
                                  value={hour.toString()}
                                  shape="circular"
                                  dismissible
                                  dismissIcon={{ 'aria-label': `Remove ${formatHour(hour)}` }}
                                >
                                  {formatHour(hour)}
                                </Tag>
                              ))}
                            </TagGroup>
                          </div>
                        )}
                        <div className={styles.inputRow}>
                          <SpinButton
                            className={styles.numberInput}
                            min={0}
                            max={23}
                            value={newHour === '' ? null : Number(newHour)}
                            displayValue={newHour}
                            onChange={(_, data) => {
                              if (typeof data.value === 'number') {
                                setNewHour(String(data.value));
                              } else if (typeof data.displayValue === 'string') {
                                const v = data.displayValue;
                                if (v === '' || (v.length <= 2 && /^\d+$/.test(v))) setNewHour(v);
                              }
                            }}
                            onKeyDown={(e) => e.key === 'Enter' && addHour()}
                            disabled={isDisabled}
                            placeholder="Hour"
                            aria-label="Add hour"
                          />
                          <Button
                            className={styles.addButton}
                            icon={<AddRegular />}
                            onClick={addHour}
                            disabled={isDisabled}
                            aria-label="Add hour"
                          />
                        </div>
                      </div>
                    </Field>

                    {/* Minutes */}
                    <Field label="At these minutes" hint="Enter minutes (0-59)">
                      <div className={styles.tagsContainer} role="group" aria-label="Selected minutes">
                        {recurrence.schedule?.minutes && recurrence.schedule.minutes.length > 0 && (
                          <div className={styles.tagsRow}>
                            <TagGroup onDismiss={handleMinuteDismiss}>
                              {recurrence.schedule.minutes.map((minute) => (
                                <Tag
                                  key={minute}
                                  value={minute.toString()}
                                  shape="circular"
                                  dismissible
                                  dismissIcon={{ 'aria-label': `Remove minute ${minute}` }}
                                >
                                  {minute.toString().padStart(2, '0')}
                                </Tag>
                              ))}
                            </TagGroup>
                          </div>
                        )}
                        <div className={styles.inputRow}>
                          <SpinButton
                            className={styles.numberInput}
                            min={0}
                            max={59}
                            value={newMinute === '' ? null : Number(newMinute)}
                            displayValue={newMinute}
                            onChange={(_, data) => {
                              if (typeof data.value === 'number') {
                                setNewMinute(String(data.value));
                              } else if (typeof data.displayValue === 'string') {
                                const v = data.displayValue;
                                if (v === '' || (v.length <= 2 && /^\d+$/.test(v))) setNewMinute(v);
                              }
                            }}
                            onKeyDown={(e) => e.key === 'Enter' && addMinute()}
                            disabled={isDisabled}
                            placeholder="Min"
                            aria-label="Add minute"
                          />
                          <Button
                            className={styles.addButton}
                            icon={<AddRegular />}
                            onClick={addMinute}
                            disabled={isDisabled}
                            aria-label="Add minute"
                          />
                        </div>
                      </div>
                    </Field>
                  </div>
                </>
              )}
            </div>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
      )}

      {/* Validation errors */}
      {errors.length > 0 && (
        <div role="alert" aria-live="assertive">
          {errors.map((err, i) => (
            <Caption1 key={i} className={styles.error}>
              {err.message}
            </Caption1>
          ))}
        </div>
      )}
    </div>
  );
};

RecurrenceField.displayName = 'RecurrenceField';


